import { UTCDate } from '@date-fns/utc';
import { keyframes, styled } from '@mui/material';
import CalendarMonth from './CalendarMonth';
import { calendarLayout } from './constants';
import { CalendarDateRange, DateRangePickerVariant } from './types';
import { Month } from './utils';

const slideInFromRight = keyframes({
    from: {
        opacity: 0.65,
        transform: 'translateX(100%)',
    },
    to: {
        opacity: 1,
        transform: 'translateX(0)',
    },
});

const slideOutToLeft = keyframes({
    from: {
        opacity: 1,
        transform: 'translateX(0)',
    },
    to: {
        opacity: 0.65,
        transform: 'translateX(-100%)',
    },
});

const slideInFromLeft = keyframes({
    from: {
        opacity: 0.65,
        transform: 'translateX(-100%)',
    },
    to: {
        opacity: 1,
        transform: 'translateX(0)',
    },
});

const slideOutToRight = keyframes({
    from: {
        opacity: 1,
        transform: 'translateX(0)',
    },
    to: {
        opacity: 0.65,
        transform: 'translateX(100%)',
    },
});

const MonthViewport = styled('div')(() => ({
    position: 'relative',
    width: calendarLayout.monthViewportWidth,
    minHeight: calendarLayout.monthViewportMinHeight,
    overflow: 'hidden',
}));

const MonthPanel = styled('div', {
    shouldForwardProp: prop =>
        !['$state', '$direction'].includes(prop as string),
})<{
    $state: 'static' | 'enter' | 'exit';
    $direction: 'next' | 'prev';
}>(({ $state, $direction }) => ({
    width: '100%',
    ...($state === 'static' && {
        position: 'relative',
    }),
    ...($state !== 'static' && {
        position: 'absolute',
        inset: 0,
        animationDuration: `${calendarLayout.transitionDurationMs}ms`,
        animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
        animationFillMode: 'forwards',
    }),
    ...($state === 'enter' &&
        $direction === 'next' && {
            animationName: slideInFromRight,
        }),
    ...($state === 'exit' &&
        $direction === 'next' && {
            animationName: slideOutToLeft,
        }),
    ...($state === 'enter' &&
        $direction === 'prev' && {
            animationName: slideInFromLeft,
        }),
    ...($state === 'exit' &&
        $direction === 'prev' && {
            animationName: slideOutToRight,
        }),
}));

interface CalendarMonthViewportProps {
    month: Month;
    transitionMonth: Month | null;
    transitionDirection: 'next' | 'prev';
    variant: DateRangePickerVariant;
    periods: CalendarDateRange[];
    previewPeriod: CalendarDateRange | null;
    disableBefore?: UTCDate;
    disableAfter?: UTCDate;
    disabledDates?: UTCDate[];
    onDayClick: (day: UTCDate) => void;
    onDayHover: (day: UTCDate | null) => void;
}

export const CalendarMonthViewport = ({
    month,
    transitionMonth,
    transitionDirection,
    variant,
    periods,
    previewPeriod,
    disableBefore,
    disableAfter,
    disabledDates,
    onDayClick,
    onDayHover,
}: CalendarMonthViewportProps) => {
    return (
        <MonthViewport>
            {transitionMonth && (
                <MonthPanel $state='exit' $direction={transitionDirection}>
                    <CalendarMonth
                        year={transitionMonth.year}
                        month={transitionMonth.month}
                        variant={variant}
                        periods={periods}
                        previewPeriod={previewPeriod}
                        disableBefore={disableBefore}
                        disableAfter={disableAfter}
                        disabledDates={disabledDates}
                        onDayClick={onDayClick}
                        onDayHover={onDayHover}
                    />
                </MonthPanel>
            )}
            <MonthPanel
                $state={transitionMonth ? 'enter' : 'static'}
                $direction={transitionDirection}>
                <CalendarMonth
                    year={month.year}
                    month={month.month}
                    variant={variant}
                    periods={periods}
                    previewPeriod={previewPeriod}
                    disableBefore={disableBefore}
                    disableAfter={disableAfter}
                    disabledDates={disabledDates}
                    onDayClick={onDayClick}
                    onDayHover={onDayHover}
                />
            </MonthPanel>
        </MonthViewport>
    );
};

export default CalendarMonthViewport;
